import React, { useState, useCallback, useEffect } from "react";
import { Form } from "react-bootstrap";

/**
 * Reużywalny komponent do wprowadzania polskiego kodu pocztowego (format XX-XXX).
 *
 * Funkcjonalność:
 * - Auto-formatowanie: "72320" → "72-320" (5 cyfr bez myślnika)
 * - Wklejanie: "72-320" lub "72320" → "72-320"
 * - Walidacja wizualna: nieprawidłowy format → czerwona ramka + komunikat
 * - Dwa tryby:
 *   - mode="form"   → ścisła walidacja, blokuje submit (isInvalid)
 *   - mode="filter"  → łagodna walidacja, tylko podpowiedź (hint)
 *
 * Props:
 * @param {string}   value       - aktualna wartość kodu pocztowego
 * @param {function} onChange    - callback(newValue) wywoływany przy zmianie
 * @param {string}   [mode]     - "form" (domyślny) lub "filter"
 * @param {string}   [placeholder] - placeholder inputa
 * @param {boolean}  [required]  - czy pole jest wymagane (tryb form)
 * @param {boolean}  [disabled]  - czy pole jest wyłączone
 * @param {string}   [size]      - rozmiar inputa ("sm", "lg")
 * @param {string}   [controlId] - id kontrolki formularza
 * @param {string}   [className] - dodatkowe klasy CSS
 */
const POSTCODE_REGEX = /^\d{2}-\d{3}$/;

const normalizePostcodeDigits = (value) =>
  String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, 5);

const formatPostcodeDigitsToMask = (digitsValue) => {
  const digits = normalizePostcodeDigits(digitsValue);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
};

/**
 * Próbuje sformatować wartość do formatu XX-XXX.
 * Formatuje TYLKO bezpieczne przypadki (dokładnie 5 cyfr bez myślnika).
 * Wszystko inne zwraca bez zmian — nie zgadujemy intencji użytkownika.
 */
const autoFormatPostcode = (value) => {
  if (!value) return value;

  // Zawsze normalizujemy do maski XX-XXX na podstawie samych cyfr.
  return formatPostcodeDigitsToMask(value);
};

/**
 * Sprawdza czy wartość jest prawidłowym polskim kodem pocztowym.
 */
const isValidPostcode = (value) => {
  if (!value) return true; // puste = brak walidacji (chyba że required)
  return POSTCODE_REGEX.test(value.trim());
};

const PostcodeInput = ({
  value,
  onChange,
  mode = "form",
  placeholder = "__-___",
  required = false,
  disabled = false,
  size,
  controlId,
  className,
}) => {
  const [touched, setTouched] = useState(false);
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(() =>
    autoFormatPostcode(value ?? "") || "",
  );
  const currentValue = isControlled ? value || "" : internalValue;

  useEffect(() => {
    if (!isControlled) return;
    setInternalValue(autoFormatPostcode(value ?? "") || "");
  }, [isControlled, value]);

  // Reset touched gdy wartość jest czyszczona z zewnątrz
  useEffect(() => {
    if (!currentValue) setTouched(false);
  }, [currentValue]);

  const emitChange = useCallback(
    (nextValue) => {
      if (!isControlled) {
        setInternalValue(nextValue);
      }
      if (typeof onChange === "function") {
        onChange(nextValue);
      }
    },
    [isControlled, onChange],
  );

  const handleChange = useCallback(
    (e) => {
      const maskedValue = formatPostcodeDigitsToMask(e.target.value);
      emitChange(maskedValue);
    },
    [emitChange],
  );

  const handleBlur = useCallback(() => {
    setTouched(true);
    // Auto-formatowanie przy opuszczeniu pola
    if (currentValue) {
      const formatted = autoFormatPostcode(currentValue);
      if (formatted !== currentValue) {
        emitChange(formatted);
      }
    }
  }, [currentValue, emitChange]);

  const handlePaste = useCallback(
    (e) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData("text");
      const formatted = formatPostcodeDigitsToMask(pasted);
      emitChange(formatted);
      setTouched(true);
    },
    [emitChange],
  );

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter") {
      setTouched(true);
    }
  }, []);

  const hasValue = !!currentValue && currentValue.trim().length > 0;
  const valid = isValidPostcode(currentValue);
  const showError = hasValue && !valid && touched;
  const showRequiredError = required && !hasValue && touched;
  const isInvalid = mode === "form" && (showError || showRequiredError);

  return (
    <div style={{ position: "relative" }}>
      <Form.Control
        type="text"
        id={controlId}
        placeholder={placeholder}
        value={currentValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onPaste={handlePaste}
  onKeyDown={handleKeyDown}
        disabled={disabled}
        size={size}
        className={className}
        isInvalid={isInvalid}
        maxLength={6}
        inputMode="numeric"
        autoComplete="postal-code"
      />
      {/* Tryb form — błąd walidacji pod polem (absolutnie pozycjonowany, nie przesuwa layoutu) */}
      {mode === "form" && showError && (
        <div className="invalid-tooltip" style={{ display: "block" }}>
          Nieprawidłowy format kodu pocztowego <br></br> XX-XXX (np. 72-320)
        </div>
      )}
      {mode === "form" && showRequiredError && !showError && (
        <div className="invalid-tooltip" style={{ display: "block" }}>
          Kod pocztowy jest wymagany.
        </div>
      )}
      {/* Tryb filter — standardowy feedback */}
      {mode === "filter" && hasValue && !valid && (
        <div className="invalid-tooltip" style={{ display: "block" }}>
          Prawidłowy format: XX-XXX
        </div>
      )}
    </div>
  );
};

// Eksport helpera do walidacji w submit
export { isValidPostcode, autoFormatPostcode };
export default PostcodeInput;
